<template>
  <div class="card">
    <div class="card-body">
      <h3 class="font-600 mb-2">
        {{ id ? 'Edit' : 'Add' }} Promotion Detail Page
      </h3>
      <ValidationObserver class="card-content" ref="observer">
        <form @submit.prevent="submit">
          <div class="row">
            <div class="col">
              <ValidationProvider
                name="Title"
                v-slot="{ errors }"
                rules="required"
              >
                <div
                  class="form-group"
                  :class="{ 'validation-warning': errors[0] }"
                >
                  <label class="required">Title</label>
                  <input
                    type="text"
                    v-model="model.title"
                    class="form-control"
                    placeholder="Enter title"
                  />
                  <small class="text-muted" v-if="errors[0]">{{
                    errors[0]
                  }}</small>
                </div>
              </ValidationProvider>
            </div>
            <div class="col">
              <ValidationProvider
                name="Slug"
                v-slot="{ errors }"
                rules="required|slug-max-words:5"
              >
                <div
                  class="form-group"
                  :class="{ 'validation-warning': errors[0] }"
                >
                  <label class="required">Slug</label>
                  <input
                    type="text"
                    v-model="model.slug"
                    class="form-control"
                    placeholder="Enter slug"
                  />
                  <small class="text-muted" v-if="errors[0]">{{
                    errors[0]
                  }}</small>
                </div>
              </ValidationProvider>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <ValidationProvider
                name="Description"
                v-slot="{ errors }"
                rules="required"
              >
                <div
                  class="form-group"
                  :class="{ 'validation-warning': errors[0] }"
                >
                  <label class="required">Description</label>
                  <quill-editor
                    v-model="model.rawDescription"
                    placeholder="Enter description"
                    :options="editorOption"
                  >
                  </quill-editor>
                  <small class="text-muted" v-if="errors[0]">{{
                    errors[0]
                  }}</small>
                </div>
              </ValidationProvider>
            </div>
          </div>
          <div class="row">
            <ValidationProvider
              name="Image"
              v-slot="{ errors, validate }"
              :rules="{ required: !model.image, 'max-dimensions': 1080 }"
              class="col-6"
            >
              <div
                class="form-group"
                :class="{ 'validation-warning': errors[0] }"
              >
                <label for="image" class="required">Image</label>
                <div class="form-control" style="height: auto">
                  <span
                    class="btn btn-outline-primary btn-sm"
                    @click="$refs.image.click()"
                    >Select Image</span
                  >
                  <img
                    v-if="model.image"
                    :src="model.image"
                    width="430"
                    height="135"
                    class="mt-1 d-block"
                    style="object-fit: cover; object-position: center"
                  />
                  <small class="block mt-1" style="margin-top: 4px"
                    >Ratio: 86:27</small
                  >
                  <small class="block" style="margin-top: 4px"
                    >Recommended dimensions: 1080 x 339 pixels</small
                  >
                  <small class="block" style="margin-top: 4px"
                    >Max width: 1080 pixels</small
                  >
                </div>
                <input
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  @change="
                    (e) => {
                      validate(e).then((res) => storeImage(res, e));
                    }
                  "
                  id="image"
                  class="hidden"
                  placeholder="Upload Image"
                  ref="image"
                />
                <small class="text-danger" v-if="errors[0]">{{
                  errors[0]
                }}</small>
              </div>
            </ValidationProvider>
            <ValidationProvider
              name="Start date and time"
              v-slot="{ errors }"
              :rules="{ required: isDateRequired }"
              class="col-3"
            >
              <div
                class="form-group"
                :class="{ 'validation-warning': errors[0] }"
              >
                <label class="required">Start Date and Time</label>
                <datetime
                  type="datetime"
                  input-class="form-control"
                  v-model="model.startDate"
                  use12-hour
                  :min-datetime="minStartDate"
                  placeholder="Enter Start Date & Time"
                  auto
                  class="theme-grey"
                >
                </datetime>
                <small class="text-muted" v-if="errors[0]">{{
                  errors[0]
                }}</small>
              </div>
            </ValidationProvider>
            <ValidationProvider
              name="End date and time"
              v-slot="{ errors }"
              :rules="{ required: isDateRequired }"
              class="col-3"
            >
              <div
                class="form-group"
                :class="{ 'validation-warning': errors[0] }"
              >
                <label class="required">End Date and Time</label>
                <datetime
                  type="datetime"
                  input-class="form-control"
                  v-model="model.endDate"
                  use12-hour
                  :min-datetime="minEndDate"
                  :disabled="!model.startDate"
                  placeholder="Enter End Date & Time"
                  auto
                  class="theme-grey"
                >
                </datetime>
                <small class="text-muted" v-if="errors[0]">{{
                  errors[0]
                }}</small>
              </div>
            </ValidationProvider>
          </div>
          <div class="row">
            <div class="col form-group">
              <label>Promotion Codes</label>
              <multiselect
                v-model="selectedPromotionVouchers"
                label="name"
                track-by="id"
                placeholder="Select Promotion Codes"
                :multiple="true"
                :options="promotionVouchers"
                :clear-on-select="true"
                :close-on-select="false"
              >
                <span slot="noResult">Oops! Data not found.</span>
              </multiselect>
            </div>
          </div>
          <div class="row">
            <div class="form-group col">
              <label>Is CTA Button Active?</label>
              <select class="form-control" v-model="model.isCtaButtonActive">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>
            <ValidationProvider
              name="CTA Button Text"
              v-slot="{ errors }"
              rules="required"
              class="col"
            >
              <div
                class="form-group"
                :class="{ 'validation-warning': errors[0] }"
              >
                <label class="required">CTA Button Text</label>
                <input
                  type="text"
                  v-model="model.ctaButtonText"
                  class="form-control"
                  placeholder="Enter CTA Button Text"
                />
                <small class="text-muted" v-if="errors[0]">{{
                  errors[0]
                }}</small>
              </div>
            </ValidationProvider>
          </div>
          <div class="row">
            <ValidationProvider
              name="CTA Button URL"
              v-slot="{ errors }"
              rules="required|url"
              class="col"
            >
              <div
                class="form-group"
                :class="{ 'validation-warning': errors[0] }"
              >
                <label class="required">CTA Button URL</label>
                <input
                  type="text"
                  v-model="model.ctaButtonUrl"
                  class="form-control"
                  placeholder="Enter CTA Button URL"
                />
                <small class="text-muted" v-if="errors[0]">{{
                  errors[0]
                }}</small>
              </div>
            </ValidationProvider>
            <ValidationProvider
              name="CTA Button Deeplink"
              v-slot="{ errors }"
              rules="url-deeplink"
              class="col"
            >
              <div
                class="form-group"
                :class="{ 'validation-warning': errors[0] }"
              >
                <label>CTA Button Deeplink</label>
                <input
                  type="text"
                  v-model="model.ctaButtonDeeplink"
                  class="form-control"
                  placeholder="Enter CTA Button Deeplink"
                />
                <small class="text-muted" v-if="errors[0]">{{
                  errors[0]
                }}</small>
              </div>
            </ValidationProvider>
          </div>
          <div class="row">
            <div class="col">
              <hr />
              <router-link to="/promotion-detail-pages" class="btn text-danger"
                >Cancel</router-link
              >
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isLoading"
              >
                <div v-if="isLoading">
                  {{ id ? 'Updating' : 'Creating'
                  }}<i class="fa fa-spinner fa-spin"></i>
                </div>
                <div v-else>{{ id ? 'Update' : 'Create' }}</div>
              </button>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
const toolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ color: ['#111820', '#0072cd', '#ff7f32', false] }],
  ['link'],
];

export default {
  data() {
    return {
      id: this.$route.params.id,
      model: {
        title: '',
        slug: '',
        rawDescription: '',
        image: null,
        startDate: null,
        endDate: null,
        isCtaButtonActive: true,
        ctaButtonText: 'Mulai Belanja',
        ctaButtonUrl: 'https://diricare.com/product',
        ctaButtonDeeplink: 'diricare://product/',
      },
      imageFile: null,
      isDateRequired: false,
      promotionVouchers: [],
      selectedPromotionVouchers: [],
      isLoading: false,
      editorOption: {
        modules: {
          toolbar: {
            container: toolbarOptions,
          },
        },
      },
    };
  },
  computed: {
    minStartDate: function () {
      return this.$moment().toISOString();
    },
    minEndDate: function () {
      return this.$moment(this.model.startDate).toISOString();
    },
  },
  async mounted() {
    this.getPromotionVouchers();
    if (this.id) {
      this.axios
        .get(`/promotion-banners/${this.id}`)
        .then((res) => {
          const { data } = res.data;
          this.model.title = data.title;
          this.model.slug = data.slug;
          this.model.rawDescription = data.rawDescription;
          this.model.image = data.image;
          this.model.startDate = this.$moment(data.startDate).toISOString();
          this.model.endDate = this.$moment(data.endDate).toISOString();
          this.model.isCtaButtonActive = data.isCtaButtonActive;
          this.model.ctaButtonText = data.ctaButtonText;
          this.model.ctaButtonUrl = data.ctaButtonUrl;
          this.model.ctaButtonDeeplink = data.ctaButtonDeeplink;
          this.selectedPromotionVouchers = data.promotions;
        })
        .catch((err) => {
          this.$notify({
            type: 'error',
            title: 'Error!',
            text: err.response.data.message,
          });
          this.$router.push('/promotion-detail-pages');
        });
    }
  },
  methods: {
    async submit() {
      this.isDateRequired = true;
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        const formDataImage = new FormData();
        formDataImage.append('folder', 'promotion');
        formDataImage.append('file', this.imageFile);

        const headers = {
          'Content-Type': 'multipart/form-data',
        };

        try {
          this.isLoading = true;

          // upload images first
          const responseImage = this.imageFile
            ? await this.axios.post('/misc/upload', formDataImage, { headers })
            : null;
          const imageUrl = responseImage
            ? responseImage.data.data.file
            : this.model.image;

          const data = {
            title: this.model.title,
            slug: this.model.slug,
            rawDescription: this.model.rawDescription,
            image: imageUrl,
            startDate: this.model.startDate,
            endDate: this.model.endDate,
            promotions: this.selectedPromotionVouchers.map(
              (promotion) => promotion.id
            ),
            isCtaButtonActive: this.model.isCtaButtonActive,
            ctaButtonText: this.model.ctaButtonText,
            ctaButtonUrl: this.model.ctaButtonUrl,
            ctaButtonDeeplink: this.model.ctaButtonDeeplink,
          };

          if (this.id) {
            this.axios
              .put(`/promotion-banners/${this.id}`, data)
              .then((resp) => {
                this.$notify({
                  type: 'success',
                  title: 'Success!',
                  text: resp.data.message,
                });
                this.$router.push('/promotion-detail-pages');
              })
              .catch((err) => {
                this.isLoading = false;
                this.$notify({
                  type: 'error',
                  title: 'Error',
                  text: err.response.data.message,
                });
              });
          } else {
            this.axios
              .post('/promotion-banners', data)
              .then((resp) => {
                this.$notify({
                  type: 'success',
                  title: 'Success!',
                  text: resp.data.message,
                });
                this.$router.push('/promotion-detail-pages');
              })
              .catch((err) => {
                this.isLoading = false;
                this.$notify({
                  type: 'error',
                  title: 'Error',
                  text: err.response.data.message,
                });
              });
          }
        } catch (err) {
          this.isLoading = false;
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Failed to upload images',
          });
        }
      }
    },
    getPromotionVouchers() {
      this.axios
        .get('promotions', {
          params: {
            type: 'purchase',
            isActive: true,
            visibilityType: 'ATL',
          },
        })
        .then((resp) => {
          this.promotionVouchers = resp.data.data.rows;
        })
        .catch((err) => {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: err.response.data.message,
          });
        });
    },
    storeImage({ valid }, event) {
      if (valid) {
        this.imageFile = event.target.files[0];
        this.$set(
          this.model,
          'image',
          window.URL.createObjectURL(event.target.files[0])
        );
      }
    },
    generateSlugFromTitle(title) {
      return title.toLowerCase().split(' ').join('-');
    },
  },
  watch: {
    'model.title': function (newValue) {
      if (!this.id) {
        this.model.slug = this.generateSlugFromTitle(newValue);
      }
    },
    'model.startDate': function (_, oldValue) {
      if (oldValue) {
        this.model.endDate = null;
      }
    },
  },
};
</script>
<style>
.theme-grey .vdatetime-popup__header {
  background: #333;
}

.theme-grey .vdatetime-calendar__month__day--selected > span > span,
.theme-grey .vdatetime-calendar__month__day--selected:hover > span > span {
  background: #0036a0;
}

.theme-grey .vdatetime-year-picker__item--selected,
.theme-grey .vdatetime-time-picker__item--selected,
.theme-grey .vdatetime-popup__actions__button {
  color: #0036a0;
}
</style>
